import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { SafeReferralAgent, AgentCoverageEntry } from "@shared/schema";
import {
  getPersonaConfig,
  resolvePersona,
  type PersonaConfig,
  type PersonaKey,
} from "@shared/agent-persona";

// Server-enriched payload returned by /api/agent/me. Keeps the typing
// honest end-to-end (no `any` narrowing) so callers reading
// `personaProfileExtras` or the seat / impersonation fields don't
// have to cast through unknown.
//
// Task #907 — Persona ships as a *key only* (string union). The full
// `PersonaConfig` carries executable `isComplete` callbacks on its
// onboarding items, and JSON serialization on the wire would silently
// drop those functions. The client re-hydrates the full config
// locally via `getPersonaConfig(personaKey)`.
interface AgentMePayload extends SafeReferralAgent {
  personaKey?: PersonaKey;
  countries?: AgentCoverageEntry[];
  languages?: AgentCoverageEntry[];
  isImpersonatingAgent?: boolean;
  impersonatingAdmin?: ImpersonatingAdminSummary | null;
  seat?: AgentSeatSummary | null;
  /** Task #907 — uploaded-document presence flags surfaced by
   * /api/agent/me so the onboarding checklist can key off actual
   * upload state instead of admin-managed metadata. */
  hasLicenseDocument?: boolean;
  hasW9Document?: boolean;
  /** Situational W-9 gating. `isUsBased` = US in agent_countries.
   * `hasPayoutActivity` = at least one accrued commission entry or
   * non-terminal scheduled payout. All W-9 surfaces (onboarding
   * step, dashboard row, profile slot/banner) key off these. */
  isUsBased?: boolean;
  hasPayoutActivity?: boolean;
  /** Task #1421 — true when the agent has no acceptance row for the
   * current terms version, surfaced by GET /api/agent/me. */
  needsTermsAcceptance?: boolean;
  /** Task #1995 — false when the authenticated seat hasn't verified its
   * email yet. Surfaced by GET /api/agent/me. Defaults to verified when
   * absent so legacy payloads don't trigger the prompt. */
  emailVerified?: boolean;
}

interface ImpersonatingAdminSummary {
  id: string;
  name: string | null;
  email: string | null;
}

// Task #698: identifies the seat (agent_users row) the caller is
// authenticated as. Drives the owner-only "Team" UI in the partner
// profile page. Synthesized to {role:'owner'} on impersonation so the
// admin sees the full UI as the partner would.
export interface AgentSeatSummary {
  id: string | null;
  role: "owner" | "member";
  email: string;
}

interface AgentAuthContextType {
  agent: SafeReferralAgent | null;
  isLoading: boolean;
  // Task #670: surfaces whether the partner portal is being viewed by
  // an impersonating admin. Drives the agent-portal banner and any
  // gating UI that wants to react to "this isn't really the partner".
  isImpersonatingAgent: boolean;
  impersonatingAdmin: ImpersonatingAdminSummary | null;
  // Task #698: seat info — {role:'owner'} unlocks the Team panel.
  seat: AgentSeatSummary | null;
  isOwner: boolean;
  // Task #907: resolved persona for the agent. Drives nav order,
  // dashboard slot layout, persona-specific profile fields, and the
  // onboarding checklist. Falls back
  // to the `default` persona for unclassified agents (and while the
  // /me query is loading) so the UI never has to null-check it.
  persona: PersonaConfig;
  /** Task #1421 — true when the agent needs to accept the current terms. */
  needsTermsAcceptance: boolean;
  /** Task #1995 — true once the authenticated seat has verified its email. */
  emailVerified: boolean;
  login: (email: string, password: string) => Promise<AgentLoginResult>;
  selectFirm: (agentUserId: string) => Promise<SafeReferralAgent>;
  // Task #1269 — mid-session firm switch. Picks a different active
  // seat owned by the same Supabase principal, without logging out.
  switchFirm: (agentUserId: string) => Promise<SafeReferralAgent>;
  logout: () => Promise<void>;
}

// Task #1249 — when the same email holds active seats at multiple
// partner firms, /api/agent/login returns a "choose your firm"
// payload instead of installing a session. The SPA renders the
// picker and POSTs the chosen `agentUserId` to /api/agent/login/select-firm.
export interface AgentFirmCandidate {
  agentUserId: string;
  agentId: string;
  companyName: string;
  role: "owner" | "member";
}

export type AgentLoginResult =
  | { kind: "loggedIn"; agent: SafeReferralAgent }
  | { kind: "firmPicker"; firms: AgentFirmCandidate[] };

const AgentAuthContext = createContext<AgentAuthContextType | null>(null);

export function AgentAuthProvider({ children }: { children: ReactNode }) {
  const { data: rawAgent, isLoading } = useQuery<AgentMePayload | null>({
    queryKey: ["/api/agent/me"],
    staleTime: Infinity,
    retry: false,
  });

  const agent: SafeReferralAgent | null = rawAgent ?? null;
  const isImpersonatingAgent = rawAgent?.isImpersonatingAgent === true;

  useEffect(() => {
    if (agent) {
      Sentry.setUser({
        id: agent.id,
        role: "agent",
        impersonating: isImpersonatingAgent || undefined,
      });
      Sentry.setTag("role", "agent");
    } else if (rawAgent === null) {
      Sentry.setUser(null);
      Sentry.setTag("role", null);
    }
  }, [agent, isImpersonatingAgent, rawAgent]);

  const impersonatingAdmin: ImpersonatingAdminSummary | null = rawAgent?.impersonatingAdmin ?? null;
  const seat: AgentSeatSummary | null = rawAgent?.seat ?? null;
  const isOwner = seat?.role === "owner";
  // Task #907: hydrate the full `PersonaConfig` locally from the
  // serializable `personaKey` string the server sent. We can't ship
  // the executable persona object over JSON (the `isComplete`
  // onboarding callbacks would be dropped), so the contract is:
  // server picks the key, client looks up the config. Falls back to
  // resolving from the agent's structured `agent_type` for any
  // stale cache that pre-dates the personaKey field, and finally to
  // the `default` persona while the /me query is still loading.
  const persona: PersonaConfig = rawAgent?.personaKey
    ? getPersonaConfig(rawAgent.personaKey)
    : agent
      ? resolvePersona({
          agentType: agent.agentType ?? null,
          adminReviewNeeded: agent.adminReviewNeeded ?? false,
        })
      : getPersonaConfig("default");

  // Mirrors resetCacheForIdentity in use-auth.tsx — wipe non-auth
  // caches on agent login/logout (including the admin "Log out from
  // partner portal" path that just ends impersonation) so the new
  // identity can't see the previous user's data.
  const resetCacheForAgentIdentity = async () => {
    await queryClient.cancelQueries();
    queryClient.setQueryData(["/api/auth/me"], null);
    queryClient.setQueryData(["/api/agent/me"], null);
    queryClient.removeQueries({
      predicate: (q) => {
        const key = q.queryKey[0];
        return key !== "/api/auth/me" && key !== "/api/agent/me";
      },
    });
  };

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }): Promise<AgentLoginResult> => {
      const res = await apiRequest("POST", "/api/agent/login", { email, password });
      const body = (await res.json()) as
        | (SafeReferralAgent & { requiresFirmPicker?: undefined })
        | { requiresFirmPicker: true; firms: AgentFirmCandidate[] };
      if ("requiresFirmPicker" in body && body.requiresFirmPicker) {
        return { kind: "firmPicker", firms: body.firms };
      }
      return { kind: "loggedIn", agent: body as SafeReferralAgent };
    },
    onSuccess: async (result) => {
      // Only reset / refetch when we actually installed an identity.
      // The firm-picker response leaves the session in an interim state
      // (no agentId yet) — selectFirmMutation handles cache reset on
      // its own success.
      if (result.kind === "loggedIn") {
        await resetCacheForAgentIdentity();
        await queryClient.invalidateQueries({ queryKey: ["/api/agent/me"] });
      }
    },
  });

  const selectFirmMutation = useMutation({
    mutationFn: async (agentUserId: string): Promise<SafeReferralAgent> => {
      const res = await apiRequest("POST", "/api/agent/login/select-firm", { agentUserId });
      return res.json() as Promise<SafeReferralAgent>;
    },
    onSuccess: async () => {
      await resetCacheForAgentIdentity();
      await queryClient.invalidateQueries({ queryKey: ["/api/agent/me"] });
    },
  });

  // Task #1269 — mid-session firm switcher. Same cache-reset rules as
  // login: every cached query was scoped to the previous firm, so we
  // wipe and refetch the agent identity.
  const switchFirmMutation = useMutation({
    mutationFn: async (agentUserId: string): Promise<SafeReferralAgent> => {
      const res = await apiRequest("POST", "/api/agent/switch-firm", { agentUserId });
      return res.json() as Promise<SafeReferralAgent>;
    },
    onSuccess: async () => {
      await resetCacheForAgentIdentity();
      await queryClient.invalidateQueries({ queryKey: ["/api/agent/me"] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/agent/logout", {});
    },
    onSuccess: async () => {
      await resetCacheForAgentIdentity();
      // Covers the impersonating-admin "Log out from partner portal"
      // path: the server ends impersonation instead of destroying the
      // session, so /api/auth/me needs to refetch to drop the banner.
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
  });

  const login = async (email: string, password: string) => {
    return loginMutation.mutateAsync({ email, password });
  };

  const selectFirm = async (agentUserId: string) => {
    return selectFirmMutation.mutateAsync(agentUserId);
  };

  const switchFirm = async (agentUserId: string) => {
    return switchFirmMutation.mutateAsync(agentUserId);
  };

  const logout = async () => {
    await logoutMutation.mutateAsync();
  };

  const needsTermsAcceptance: boolean =
    rawAgent?.needsTermsAcceptance === true && !isImpersonatingAgent;

  // Task #1995 — treat email as verified unless the payload explicitly
  // says otherwise (legacy payloads omit the flag) and never nag an
  // impersonating admin.
  const emailVerified: boolean =
    rawAgent?.emailVerified !== false || isImpersonatingAgent;

  return (
    <AgentAuthContext.Provider
      value={{ agent, isLoading, isImpersonatingAgent, impersonatingAdmin, seat, isOwner, persona, needsTermsAcceptance, emailVerified, login, selectFirm, switchFirm, logout }}
    >
      {children}
    </AgentAuthContext.Provider>
  );
}

export function useAgentAuth() {
  const ctx = useContext(AgentAuthContext);
  if (!ctx) throw new Error("useAgentAuth must be used within AgentAuthProvider");
  return ctx;
}

/**
 * Task #907 — Single typed access point for the resolved persona.
 *
 * UI that only needs the persona (sidebar nav, dashboard slot order,
 * persona-specific profile fields, the onboarding checklist) should
 * consume this hook instead of pulling
 * the full `useAgentAuth()` context. Keeps persona-driven branches
 * consistent and makes it obvious at a glance which surfaces depend
 * on the persona vs. the broader agent / seat / impersonation state.
 *
 * Always returns a `PersonaConfig` — falls back to the `default`
 * persona while /api/agent/me is loading and for unclassified /
 * pending-review agents (handled inside `AgentAuthProvider`), so
 * callers never have to null-check it.
 */
export function usePersona(): PersonaConfig {
  return useAgentAuth().persona;
}
